import type { StatementItem, UnitDetails } from '../api/types'
import {
    balanceStatus,
    formatMoney,
    formatShortDate,
    joinAddress,
    parseBalance,
} from '../lib/format'
import { KeyValue, Pill, Row, RowMeta, RowTitle, Section } from './ui'

/** How many recent statement lines to show; the API already caps at 6 per fee type. */
const STATEMENT_LINES = 3

export function SenderAndUnitSection({ details }: { details: UnitDetails }) {
    const unitAddress = joinAddress([
        details.unit_address_1,
        details.unit_address_2,
        details.unit_address_3,
        details.unit_address_town,
        details.unit_address_post_code,
    ])

    const rows: [string, React.ReactNode][] = []
    if (details.person_company_name) rows.push(['Legal owner', details.person_company_name])
    if (details.tenant_reference) rows.push(['Tenant ref', details.tenant_reference])
    if (details.pm_name) rows.push(['Manager', details.pm_name])

    return (
        <Section title="Sender &amp; unit">
            <div className="mb-1.5">
                <div className="text-[13px] font-bold">{details.unit_name}</div>
                {unitAddress && <div className="mt-px text-[10.5px] text-foreground/55">{unitAddress}</div>}
            </div>
            {rows.length > 0 && <KeyValue rows={rows} />}
            {details.notes && (
                <p className="mt-2 mb-0 border-t border-border pt-1.5 text-[10.5px] text-foreground/60">
                    {details.notes}
                </p>
            )}
        </Section>
    )
}

function StatementLines({ items }: { items: StatementItem[] | undefined }) {
    const lines = (items ?? []).slice(0, STATEMENT_LINES)
    if (lines.length === 0) return null

    return (
        <div className="border-t border-border pt-1.5">
            <div className="mb-1 text-[10.5px] text-foreground/55">Recent activity</div>
            {lines.map((item, index) => (
                <div
                    key={`${item.date}-${index}`}
                    className="flex items-baseline justify-between gap-2 text-[10.5px]"
                >
                    <span className="truncate">
                        {formatShortDate(item.date) ?? item.date}
                        {item.description ? ` — ${item.description}` : ''}
                    </span>
                    <span className="flex-none font-bold tabular-nums">{formatMoney(item.amount)}</span>
                </div>
            ))}
        </div>
    )
}

export function FinancialSection({ details }: { details: UnitDetails }) {
    const serviceCharge = parseBalance(details.service_charge_balance)
    const groundRent = parseBalance(details.ground_rent_balance)
    const scStatus = balanceStatus(serviceCharge)
    const grStatus = balanceStatus(groundRent)

    return (
        <Section title="Financial position">
            <Row
                main={
                    <>
                        <RowTitle>Service charge</RowTitle>
                        <RowMeta>Balance outstanding</RowMeta>
                    </>
                }
                aside={
                    <>
                        <div
                            className={`font-bold tabular-nums ${
                                scStatus.owed ? 'text-[hsl(4_72%_42%)]' : 'text-[hsl(145_55%_30%)]'
                            }`}
                        >
                            {formatMoney(serviceCharge)}
                        </div>
                        <Pill tone={scStatus.tone}>{scStatus.label}</Pill>
                    </>
                }
            />

            {/* Many blocks are service-charge only; BOL2 tells us when ground rent applies. */}
            {details.show_ground_rent && (
                <Row
                    main={
                        <>
                            <RowTitle>Ground rent</RowTitle>
                            <RowMeta>Balance outstanding</RowMeta>
                        </>
                    }
                    aside={
                        <>
                            <div
                                className={`font-bold tabular-nums ${
                                    grStatus.owed ? 'text-[hsl(4_72%_42%)]' : 'text-[hsl(145_55%_30%)]'
                                }`}
                            >
                                {formatMoney(groundRent)}
                            </div>
                            <Pill tone={grStatus.tone}>{grStatus.label}</Pill>
                        </>
                    }
                />
            )}

            <div className="mt-1.5">
                <StatementLines items={details.service_charge_statement} />
            </div>
        </Section>
    )
}

export function CreditControlSection({ details }: { details: UnitDetails }) {
    const cases = details.credit_control_cases ?? []
    if (cases.length === 0) return null

    return (
        <Section title="Credit control" count={`${cases.length} open`}>
            {cases.map((creditCase) => {
                const nextAction = formatShortDate(creditCase.next_action_date)
                const meta = [nextAction ? `Next action ${nextAction}` : null, creditCase.user_name]
                    .filter(Boolean)
                    .join(' · ')

                return (
                    <Row
                        key={creditCase.ccc_id}
                        main={
                            <>
                                <RowTitle>{creditCase.status_description}</RowTitle>
                                {meta && <RowMeta>{meta}</RowMeta>}
                            </>
                        }
                        aside={
                            <>
                                <div className="font-bold tabular-nums text-[hsl(4_72%_42%)]">
                                    {formatMoney(parseBalance(creditCase.amount_to_chase))}
                                </div>
                                <Pill tone="amber">Chasing</Pill>
                            </>
                        }
                    />
                )
            })}
        </Section>
    )
}

const PRIORITY_TONES: Record<string, 'red' | 'amber' | 'blue' | 'grey'> = {
    high: 'red',
    urgent: 'red',
    medium: 'blue',
    low: 'grey',
}

export function TicketsSection({
    details,
    highlightTicketId,
}: {
    details: UnitDetails
    highlightTicketId?: number
}) {
    const tickets = details.tickets ?? []
    const openCount = tickets.filter((ticket) => ticket.is_open).length

    // Open tickets first — they're what a manager needs before replying.
    const ordered = [...tickets].sort((a, b) => Number(b.is_open) - Number(a.is_open))

    return (
        <Section title="Tickets" count={tickets.length === 0 ? 0 : `${openCount} open`}>
            {ordered.length === 0 ? (
                <p className="m-0 text-[10.5px] text-foreground/55">No tickets for this unit.</p>
            ) : (
                ordered.map((ticket) => {
                    const isNew = ticket.id === highlightTicketId
                    const meta = [
                        `#${ticket.id}`,
                        isNew ? 'just now' : ticket.status_description,
                        !isNew && ticket.action_date ? `action ${formatShortDate(ticket.action_date)}` : null,
                    ]
                        .filter(Boolean)
                        .join(' · ')

                    return (
                        <div
                            key={ticket.id}
                            className={isNew ? '-mx-2.5 bg-success/[0.08] px-2.5' : undefined}
                        >
                            <Row
                                main={
                                    <>
                                        <RowTitle>{ticket.title ?? 'Untitled ticket'}</RowTitle>
                                        <RowMeta>{meta}</RowMeta>
                                    </>
                                }
                                aside={
                                    isNew ? (
                                        <Pill tone="green">New</Pill>
                                    ) : ticket.priority ? (
                                        <Pill tone={PRIORITY_TONES[ticket.priority.toLowerCase()] ?? 'grey'}>
                                            {ticket.priority}
                                        </Pill>
                                    ) : undefined
                                }
                            />
                        </div>
                    )
                })
            )}
        </Section>
    )
}

export function PeopleSection({
    details,
    senderEmail,
}: {
    details: UnitDetails
    senderEmail: string | undefined
}) {
    const people = details.people_involved ?? []
    if (people.length === 0) return null

    const senderKey = senderEmail?.toLowerCase()

    return (
        <Section title="People involved" count={people.length} defaultOpen={false}>
            {people.map((person, index) => {
                // Highlighting the sender's own row makes it obvious who you're replying to.
                const isSender = Boolean(senderKey && person.email_id?.toLowerCase() === senderKey)
                const contact = [person.email_id, person.mobile_number ?? person.telephone_number]
                    .filter(Boolean)
                    .join(' · ')

                return (
                    <div
                        key={`${person.person_company_name}-${index}`}
                        className={
                            isSender ? '-mx-2.5 border-l-2 border-action bg-action/[0.08] px-2.5' : undefined
                        }
                    >
                        <Row
                            main={
                                <>
                                    <RowTitle>
                                        {person.person_company_name}{' '}
                                        {isSender && <Pill tone="blue">Sender</Pill>}
                                        {person.is_management_team && <Pill tone="grey">Mgmt team</Pill>}
                                    </RowTitle>
                                    <RowMeta>
                                        {person.rel_type_description}
                                        {contact ? ` · ${contact}` : ''}
                                    </RowMeta>
                                </>
                            }
                        />
                    </div>
                )
            })}
        </Section>
    )
}

export function PortalAccessSection({ details }: { details: UnitDetails }) {
    const users = details.portal_users ?? []
    if (users.length === 0) return null

    return (
        <Section title="Portal access" count={users.length} defaultOpen={false}>
            {users.map((user) => {
                const lastLogin = formatShortDate(user.last_login_date)
                const delivery = user.svc_via_email
                    ? 'demands by email'
                    : user.svc_via_paper
                      ? 'demands by post'
                      : null

                return (
                    <Row
                        key={user.pcm_id}
                        main={
                            <>
                                <RowTitle>{user.person_company_name ?? user.email ?? 'Unknown'}</RowTitle>
                                <RowMeta>
                                    {[lastLogin ? `Last login ${lastLogin}` : 'Never logged in', delivery]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </RowMeta>
                            </>
                        }
                        aside={
                            lastLogin ? <Pill tone="green">Active</Pill> : <Pill tone="grey">Dormant</Pill>
                        }
                    />
                )
            })}
        </Section>
    )
}
